/**
 * Market row extras (M4 spec §12.3, §15 item 3): the price cause chip, the stock bar and the trend arrow. Every
 * signal carries a glyph or text as well as its tone (style guide §9.5: colour is never the only carrier).
 */
import type { MarketRow, PriceCause, PriceCauseTag } from '../../strategic/types'

export const CAUSE_TAG_LABELS: Readonly<Record<PriceCauseTag, string>> = Object.freeze({
  supplyShock: 'Supply shock',
  convoyLost: 'Convoy lost',
  raid: 'Raid',
  inputShortage: 'Input shortage',
  demand: 'Demand',
  dump: 'Dumped stock',
  oversupply: 'Oversupply',
})

export type Tone = 'up' | 'down' | 'flat'

export interface CauseChipModel {
  tag: PriceCauseTag
  /** "▲ +24%" (the sign glyph and the signed percent). */
  label: string
  /** "Supply shock". */
  tagLabel: string
  /** The tooltip: the cause text ("+24% supply shock: convoy lost near Varos"). */
  tooltip: string
  sign: -1 | 0 | 1
  tone: Tone
}

/** "+24%", "−12%" or "0%" (true minus sign). */
export function signedPct(pct: number): string {
  const r = Math.round(pct)
  if (r === 0) return '0%'
  return r > 0 ? `+${r}%` : `−${-r}%`
}

/** The chip for a row's cause, or null when the price has no cause. */
export function causeChipModel(cause: PriceCause | null | undefined): CauseChipModel | null {
  if (cause === null || cause === undefined) return null
  const r = Math.round(cause.deltaPct)
  const sign: -1 | 0 | 1 = r > 0 ? 1 : r < 0 ? -1 : 0
  const glyph = sign > 0 ? '▲' : sign < 0 ? '▼' : '◆'
  return {
    tag: cause.tag,
    label: `${glyph} ${signedPct(cause.deltaPct)}`,
    tagLabel: CAUSE_TAG_LABELS[cause.tag],
    tooltip: cause.text.length > 0 ? cause.text : `${signedPct(cause.deltaPct)} ${CAUSE_TAG_LABELS[cause.tag].toLowerCase()}`,
    sign,
    tone: sign > 0 ? 'up' : sign < 0 ? 'down' : 'flat',
  }
}

export interface TrendModel {
  glyph: string
  label: string
  tone: Tone
}

/** The price trend vs the 3-day baseline. */
export function trendModel(trend: -1 | 0 | 1): TrendModel {
  if (trend > 0) return { glyph: '↑', label: 'Price rising', tone: 'up' }
  if (trend < 0) return { glyph: '↓', label: 'Price falling', tone: 'down' }
  return { glyph: '→', label: 'Price steady', tone: 'flat' }
}

export interface StockModel {
  /** 0..1. */
  fill: number
  /** "42% stocked". */
  label: string
  /** "420 / 1,000 t". */
  detail: string
  tone: 'ok' | 'warning' | 'danger'
}

const LOW_FILL = 0.1
const HIGH_FILL = 0.95

/** The stock bar: fill, its label, and a tone at the Wire's shortage (< 10%) and surplus (> 95%) lines. */
export function stockModel(row: Pick<MarketRow, 'stock' | 'capacity' | 'fill'>): StockModel {
  const fill = Number.isFinite(row.fill) ? Math.min(1, Math.max(0, row.fill)) : 0
  const pct = Math.round(fill * 100)
  const word = fill < LOW_FILL ? 'short' : fill > HIGH_FILL ? 'glutted' : 'stocked'
  return {
    fill,
    label: `${pct}% ${word}`,
    detail: `${Math.round(row.stock).toLocaleString('en-US')} / ${Math.round(row.capacity).toLocaleString('en-US')} t`,
    tone: fill < LOW_FILL ? 'danger' : fill > HIGH_FILL ? 'warning' : 'ok',
  }
}

/** Everything a market row shows from the strategic economy. */
export interface RowEconomy {
  stock: StockModel
  trend: TrendModel
  cause: CauseChipModel | null
}

export function rowEconomy(row: MarketRow): RowEconomy {
  return { stock: stockModel(row), trend: trendModel(row.trend), cause: causeChipModel(row.cause) }
}
